import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { StatsStrip } from "@/components/admin/stats-strip";
import { ProviderTestButton } from "@/components/admin/provider-test-button";
import { testDomainRegistrar } from "@/lib/provider-tests.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Globe, CheckCircle2, Search, Percent, FlaskConical } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/domain-registrars")({
  component: DomainRegistrarsAdmin,
});

type ProviderId = "namesilo" | "namecheap" | "resellerclub";

type RegistrarConfig = {
  enabled: boolean;
  sandbox: boolean;
  api_key: string;
  api_secret: string;
  username: string;
  reseller_id: string;
  markup_percent: number;
};

type RegistrarsSettings = {
  default_provider: ProviderId;
  providers: Record<ProviderId, RegistrarConfig>;
};

const PROVIDER_META: Record<ProviderId, { name: string; tagline: string; docsUrl: string; fields: Array<keyof RegistrarConfig> }> = {
  namesilo: {
    name: "NameSilo",
    tagline: "Lowest TLD wholesale pricing, free WHOIS privacy, simple REST API.",
    docsUrl: "https://www.namesilo.com/api-reference",
    fields: ["api_key"],
  },
  namecheap: {
    name: "Namecheap",
    tagline: "Popular registrar with reseller program and broad TLD coverage.",
    docsUrl: "https://www.namecheap.com/support/api/intro/",
    fields: ["api_key", "username"],
  },
  resellerclub: {
    name: "ResellerClub",
    tagline: "Wholesale registrar for resellers — 500+ TLDs, hosting & SSL bundled.",
    docsUrl: "https://manage.resellerclub.com/kb/answer/744",
    fields: ["api_key", "reseller_id"],
  },
};

const DEFAULT_CONFIG: RegistrarConfig = {
  enabled: false,
  sandbox: true,
  api_key: "",
  api_secret: "",
  username: "",
  reseller_id: "",
  markup_percent: 15,
};

const DEFAULT_SETTINGS: RegistrarsSettings = {
  default_provider: "namesilo",
  providers: {
    namesilo: { ...DEFAULT_CONFIG },
    namecheap: { ...DEFAULT_CONFIG },
    resellerclub: { ...DEFAULT_CONFIG },
  },
};

function DomainRegistrarsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["site_settings", "domain_registrars"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("value").eq("key", "domain_registrars").maybeSingle();
      const raw = (data?.value as Partial<RegistrarsSettings>) || {};
      return {
        default_provider: raw.default_provider || DEFAULT_SETTINGS.default_provider,
        providers: {
          namesilo: { ...DEFAULT_CONFIG, ...(raw.providers?.namesilo || {}) },
          namecheap: { ...DEFAULT_CONFIG, ...(raw.providers?.namecheap || {}) },
          resellerclub: { ...DEFAULT_CONFIG, ...(raw.providers?.resellerclub || {}) },
        },
      } as RegistrarsSettings;
    },
  });

  const [state, setState] = useState<RegistrarsSettings>(DEFAULT_SETTINGS);
  useEffect(() => { if (data) setState(data); }, [data]);

  const save = useMutation({
    mutationFn: async (value: RegistrarsSettings) => {
      const { error } = await supabase.from("site_settings").upsert({ key: "domain_registrars", value: value as any });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_settings", "domain_registrars"] });
      toast.success("Registrar settings saved");
    },
    onError: (e: any) => toast.error(e.message || "Failed to save"),
  });

  const updateProvider = (id: ProviderId, patch: Partial<RegistrarConfig>) => {
    setState((s) => ({ ...s, providers: { ...s.providers, [id]: { ...s.providers[id], ...patch } } }));
  };

  const ids = Object.keys(state.providers) as ProviderId[];
  const enabledCount = ids.filter((p) => state.providers[p].enabled).length;
  const sandboxCount = ids.filter((p) => state.providers[p].enabled && state.providers[p].sandbox).length;
  const avgMarkup = useMemo(() => {
    const enabled = ids.filter((p) => state.providers[p].enabled);
    if (!enabled.length) return 0;
    return Math.round(enabled.reduce((s, p) => s + (state.providers[p].markup_percent || 0), 0) / enabled.length);
  }, [ids, state.providers]);

  const [query, setQuery] = useState("");
  const filteredIds = ids.filter((id) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return PROVIDER_META[id].name.toLowerCase().includes(q) || id.includes(q);
  });

  const testFn = useServerFn(testDomainRegistrar);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Domain registrars"
        description="Resell domains through wholesale registrars. Enable providers, set markup, and pick the default used for new orders."
      />

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-xl bg-muted/40" />
      ) : (
        <>
          <StatsStrip
            tiles={[
              { label: "Providers", value: ids.length, icon: Globe },
              { label: "Enabled", value: enabledCount, icon: CheckCircle2, tone: enabledCount ? "success" : "warning" },
              { label: "Sandbox", value: sandboxCount, icon: FlaskConical, tone: "warning" },
              { label: "Avg markup", value: `${avgMarkup}%`, icon: Percent, tone: "info" },
            ]}
          />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> Default registrar</CardTitle>
              <CardDescription>
                {enabledCount === 0
                  ? "No providers enabled yet. Enable at least one below before accepting domain orders."
                  : `Used as the upstream registrar when customers register a domain.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-end gap-3">
              <div className="grid min-w-0 flex-1 gap-1.5 sm:flex-initial">
                <Label>Provider</Label>
                <Select value={state.default_provider} onValueChange={(v) => setState((s) => ({ ...s, default_provider: v as ProviderId }))}>
                  <SelectTrigger className="sm:w-[220px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PROVIDER_META) as ProviderId[]).map((p) => (
                      <SelectItem key={p} value={p} disabled={!state.providers[p].enabled}>
                        {PROVIDER_META[p].name}{state.providers[p].enabled ? "" : " (disabled)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Label>Search</Label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-8" placeholder="Filter providers…" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
              </div>
              <Button onClick={() => save.mutate(state)} disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save all changes"}
              </Button>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredIds.map((id) => {
              const meta = PROVIDER_META[id];
              const cfg = state.providers[id];
              return (
                <Card key={id} className={cfg.enabled ? "border-primary/40" : ""}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {meta.name}
                          {cfg.enabled && <Badge variant="secondary">Enabled</Badge>}
                          {cfg.sandbox && cfg.enabled && <Badge variant="outline">Sandbox</Badge>}
                        </CardTitle>
                        <CardDescription className="mt-1">{meta.tagline}</CardDescription>
                      </div>
                      <Switch checked={cfg.enabled} onCheckedChange={(v) => updateProvider(id, { enabled: v })} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {meta.fields.includes("api_key") && (
                      <div className="grid gap-1.5">
                        <Label>API key</Label>
                        <Input type="password" placeholder="••••••••" value={cfg.api_key} onChange={(e) => updateProvider(id, { api_key: e.target.value })} />
                      </div>
                    )}
                    {meta.fields.includes("username") && (
                      <div className="grid gap-1.5">
                        <Label>API username</Label>
                        <Input value={cfg.username} onChange={(e) => updateProvider(id, { username: e.target.value })} />
                      </div>
                    )}
                    {meta.fields.includes("reseller_id") && (
                      <div className="grid gap-1.5">
                        <Label>Reseller ID</Label>
                        <Input value={cfg.reseller_id} onChange={(e) => updateProvider(id, { reseller_id: e.target.value })} />
                      </div>
                    )}
                    <div className="grid gap-1.5">
                      <Label>Markup over wholesale (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={cfg.markup_percent}
                        onChange={(e) => updateProvider(id, { markup_percent: Number(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-muted-foreground">Added to upstream cost when displaying TLD prices.</p>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium">Sandbox mode</p>
                        <p className="text-xs text-muted-foreground">Use test endpoints — no real registrations.</p>
                      </div>
                      <Switch checked={cfg.sandbox} onCheckedChange={(v) => updateProvider(id, { sandbox: v })} />
                    </div>
                    <ProviderTestButton
                      onTest={() => testFn({ data: { provider: id, sandbox: cfg.sandbox, api_key: cfg.api_key, api_secret: cfg.api_secret, username: cfg.username, reseller_id: cfg.reseller_id } })}
                    />
                    <a href={meta.docsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      API docs <ExternalLink className="h-3 w-3" />
                    </a>
                  </CardContent>
                </Card>
              );
            })}
            {filteredIds.length === 0 && (
              <Card className="sm:col-span-2 xl:col-span-3">
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  No providers match your search.
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => save.mutate(state)} disabled={save.isPending} size="lg">
              {save.isPending ? "Saving…" : "Save all changes"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
